import {
  StyledTableCell,
  StyledTableSortLabel,
} from '../../components/TableTheme';
import { useAuth } from '../../context/auth-context';
import { OrderEnum, SortEnum } from '../../types/pagination.types';
import { useSnack } from '../../util/useSnack';
import { useTable } from '../../util/useTable';
import {
  LinearProgress,
  TablePagination,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useEffect, useState } from 'react';
import {
  ActivityLogResponse,
  useGetActivityLogs,
} from '../../api-http/activityLogs';
import { Info } from 'react-feather';
import { DialogModal } from '../../components';
import { ViewLog } from './components/ViewLogInfo';
import { managementTableSx } from '../../components/ManagementPageLayout';

interface ComponentProps {
  filterProps: {
    startDate?: string;
    endDate?: string;
    logType?: string;
    authoredUser?: string;
  };
}

const actionButtonSx = {
  width: 32,
  height: 32,
  border: '1px solid #dbe3ee',
  borderRadius: 2,
};

const ActivityLogsTable: React.FC<ComponentProps> = ({
  filterProps: { logType, authoredUser, startDate, endDate },
}) => {
  const [activeLog, setActiveLog] = useState<ActivityLogResponse | null>(null);
  const { switchedOrgId } = useAuth();
  const { showErrSnack } = useSnack();
  const { sortOnClick, pagingParams, tableProps } = useTable();
  const { sortby, order } = pagingParams;
  const [viewLogModal, setViewLogModal] = useState<boolean>(false);

  const {
    data: logList,
    isLoading: loadLogs,
    error: errLogs,
    refetch: refetchLogs,
    isRefetching,
  } = useGetActivityLogs({
    ...pagingParams,
    startDate,
    endDate,
    logType,
    authoredUser,
  });

  useEffect(() => {
    if (errLogs) {
      showErrSnack(errLogs.msg);
    }
  }, [errLogs]);

  useEffect(() => {
    refetchLogs();
  }, [switchedOrgId]);

  const isLinerLoading = loadLogs || isRefetching;

  const handleClick = (activityLog: ActivityLogResponse) => {
    setActiveLog(activityLog);
    setViewLogModal(true);
  };

  return (
    <>
      {isLinerLoading && <LinearProgress />}
      <TableContainer component={Paper} sx={managementTableSx}>
        <Table sx={{ minWidth: 760 }} aria-label="Activity and audit logs">
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <StyledTableSortLabel
                  active={sortby === SortEnum.createdAt || true}
                  direction={
                    sortby === SortEnum.createdAt ? order : OrderEnum.asc
                  }
                  onClick={() => sortOnClick(SortEnum.createdAt)}
                >
                  Date & time
                </StyledTableSortLabel>
              </StyledTableCell>
              <StyledTableCell>Log type</StyledTableCell>

              <StyledTableCell>Action</StyledTableCell>

              <StyledTableCell>Authored by</StyledTableCell>
              <StyledTableCell align="center">Details</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!logList?.data?.length && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                  {isLinerLoading ? <CircularProgress /> : 'No Logs Found '}
                </TableCell>
              </TableRow>
            )}
            {logList?.data.map((log) => {
              return (
                <TableRow key={log._id} sx={{ height: 56 }}>
                  <TableCell>
                    {new Date(log.createdAt).toLocaleString('en-US', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                      hour: 'numeric',
                      minute: 'numeric',
                    })}
                  </TableCell>

                  <TableCell>{log.collectionName}</TableCell>
                  <TableCell>{log.action}</TableCell>
                  <TableCell>{log.requestedUser?.name || '—'}</TableCell>
                  <TableCell align="center">
                    <Tooltip title="View log details">
                      <IconButton
                        aria-label={`View details for log ${log._id}`}
                        onClick={() => handleClick(log)}
                        sx={{ ...actionButtonSx, color: '#2563eb' }}
                      >
                        <Info size={15} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          {...tableProps}
          component="div"
          hidden={logList?.pagination.total === 0}
          count={logList?.pagination.total || 0}
        />
      </TableContainer>

      <DialogModal
        title="Activity details"
        onClose={() => setViewLogModal(false)}
        open={viewLogModal}
        width={620}
      >
        <ViewLog log={activeLog} />
      </DialogModal>
    </>
  );
};

export default ActivityLogsTable;
